import FileSaver from "file-saver";
import baseUrlConfig from "./_config/baseUrl.json";
import isEqual from "lodash.isequal";
import cloneDeep from "lodash.clonedeep";
import { getSecuritiesDates } from "@/common/timePeriod";
import { checkEqualFrontLoadFees, isEqualRedemptionChargeFee } from "@/views/Settings/store/modules/fees.js";

export function importModules(context, schema) {
    return context.keys().reduce((defs, key) => {
        return Object.assign(defs, schema(key, context(key)));
    }, {});
}

export function getValueByPath(path, obj) {
    const pathSegmentArr = path.split(".");
    return pathSegmentArr.reduce((prev, k) => {
        return prev[k];
    }, obj);
}

export function setValueByPath(path, obj, value) {
    const pathSegmentArr = path.split(".");
    for (let i = 0, len = pathSegmentArr.length - 1; i < len; i++) {
        obj = obj[pathSegmentArr[i]];
    }
    obj[pathSegmentArr[pathSegmentArr.length - 1]] = value;
}

export function openNewTab(template) {
    const defaultTemplate = `<section>
                                <div role='progressbar' class='mds-loader mds-loader' style="height:50%">
                                <div class='mds-loader__item mds-loader__item--1'></div>
                                <div class='mds-loader__item mds-loader__item--2'></div>
                                <div class='mds-loader__item mds-loader__item--3'></div>
                                <div class='mds-loader__item mds-loader__item--4'></div>
                                <div class='mds-loader__item mds-loader__item--5'></div>
                                <div class='mds-loader__item mds-loader__item--6'></div>
                                <div class='mds-loader__item mds-loader__item--7'></div>
                                <div class='mds-loader__item mds-loader__item--8'></div>
                                </div>
                            </section>`;

    const tabOpen = window.open();
    tabOpen.document.body.innerHTML = template || defaultTemplate;
    var style = tabOpen.document.createElement("link");
    style.setAttribute("rel", "stylesheet");
    style.setAttribute("href", "https://mwc-cdn.morningstar.com/mc/morningstar-design-system/2.31.0/dist/mds.css");
    style.setAttribute("type", "text/css");
    tabOpen.document.head.appendChild(style);
    return tabOpen;
}

export function isTriggerDropdownLoad() {
    const documentHeight = document.documentElement.clientHeight;
    const documentScrollHeight = document.documentElement.scrollHeight;
    const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
    // When the scroll bar doesn't appear,
    // scrollTop === 0, but documentScrollHeight and documentHeight sometimes are not equal,
    // here we set the error correction to be 2
    return documentScrollHeight - documentHeight - scrollTop < 2;
}

export function showPDF(tab, pdfData) {
    const blob = createBlob(pdfData);
    var fileUrl = URL.createObjectURL(blob);
    tab.location = fileUrl;
}

export function isMSIEOrEdgeBrowser() {
    return window.navigator && window.navigator.msSaveOrOpenBlob ? true : false;
}

function createBlob(data, options = {}, type = "application/pdf") {
    return new Blob([data], {
        ...options,
        type
    });
}

export function showPDFInIE(pdfData, defaultPdfName) {
    const blob = createBlob(pdfData);
    window.navigator.msSaveOrOpenBlob(blob, `${defaultPdfName || new Date().getTime()}.pdf`);
}

export function downloadPDF(data, name) {
    const blob = createBlob(data);

    const fileName = name || new Date().getTime();
    FileSaver.saveAs(blob, `${fileName}.pdf`);
}

export function convertMinusSign(val) {
    var stringed = val.toString();
    if (stringed === "NaN") {
        return "&mdash;";
    }

    return stringed.replace(/-/g, "&minus;");
}

export function generateUUID() {
    var d = new Date().getTime();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

export function cloneObject(object) {
    return cloneDeep(object);
}

export function getDateBeforeYears(d, offYear) {
    const date = new Date(d);
    let newDate = new Date(date.getFullYear() - offYear, date.getMonth(), date.getDate());
    // Deal with leap year
    // for example: last year of "2016-02-29" is "2015-02-29",
    //              the date does not exist, it will return "2015-03-01",
    //              get the last date
    if (newDate.getMonth() !== date.getMonth()) {
        newDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate() - 1);
    }
    return getDateString(newDate);
}

export function getCheckedMonthEnd(date) {
    const currentMonthEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    if (
        dateDifference(date, currentMonthEndDate) === 0 ||
        (date.getDay() === 5 && dateDifference(date, currentMonthEndDate) <= 2) ||
        (date.getDay() === 6 && dateDifference(date, currentMonthEndDate) === 1)
    ) {
        return currentMonthEndDate;
    }
    // return last month end date
    return new Date(date.getFullYear(), date.getMonth(), 0);
}

export function dateDifference(date1, date2) {
    const newDate1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const newDate2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    const dateSpan = Math.abs(newDate1 - newDate2);
    return Math.floor(dateSpan / (24 * 3600 * 1000));
}

export function isNumber(str) {
    const reg = /^-?[\d,]+(.[\d]+)?$/; // match the numbers such as '123', '-123', '123.00', '123,456.00'
    return reg.test(str);
}

export function parseNumber(str, decimal = 2) {
    // keep 2 decimal places
    if (isNumber(str)) {
        const num = parseFloat(str.replace(/,/g, ""));
        return roundNumber(num, decimal);
    }
    return NaN;
}

export function roundNumber(number, precision = 2) {
    //use number exponential notation to avoid 1.005 round to 1
    const precisionNumber = Number(`${number}e${precision}`);
    return Math.round(precisionNumber) / Math.pow(10, precision);
}

export function getDateString(date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const d = date.getDate();
    const monthStr = addLeadingZeros(month, 2);
    const dayStr = addLeadingZeros(d, 2);
    const yearStr = addLeadingZeros(year, 4);
    return `${yearStr}-${monthStr}-${dayStr}T00:00:00`;
}

function addLeadingZeros(number, targetLength) {
    var sign = number < 0 ? "-" : "";
    var output = Math.abs(number).toString();

    while (output.length < targetLength) {
        output = `0${output}`;
    }

    return sign + output;
}

export function getAvailableMonthEndDate(dateString) {
    const date = new Date(dateString);
    const thisMonthEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    //if dateString is month end date, return dateString;
    //else if dateString is Friday && dateString + 1 day is next month, return dateString;
    //else if dateString is Friday && dateString + 2 day is next month, return dateString;
    //else return last month end date.
    const lastDay = thisMonthEndDate.getDate();
    if (lastDay === date.getDate() || (date.getDay() === 5 && (date.getDate() + 1 === lastDay || date.getDate() + 2 === lastDay))) {
        return dateString;
    } else {
        const lastMonthEndDate = new Date(date.getFullYear(), date.getMonth(), 0);
        return getDateString(lastMonthEndDate);
    }
}

export function getApiBaseUrl(url, environment, useProxy) {
    if (useProxy || isAbsoluteUrl(url) || !environment) {
        return url;
    } else {
        const apiEnv = environment.toUpperCase().split("-")[0];
        const apiBaseUrl = baseUrlConfig[apiEnv] && baseUrlConfig[apiEnv].apiUrl;
        return `${apiBaseUrl}${url}`;
    }
}

export function isAbsoluteUrl(url) {
    return /^https?:/i.test(`${url}`);
}

export function getOrigin() {
    return window.location.origin;
}

export function setLocation(href) {
    window.location.href = encodeURI(href);
}

export function getPCTEnv(environment) {
    const env = environment && environment.toUpperCase().split("-")[0];
    const pctEnvMap = {
        PROD: "PROD",
        UAT: "UAT"
    };
    return pctEnvMap[env] || "STG";
}

export const isValidDate = date => {
    const type = Object.prototype.toString.call(date);
    if (type === "[object Date]" && !isNaN(date.getTime())) {
        return true;
    }
    return false;
};

export const isSupportedDateFormat = strDate => {
    const [month, day, year] = strDate.split(/[-/]/).map(i => Number(i));
    if (month && day && year) {
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day) {
            return true;
        }
    }
    return false;
};

export const isDateFormatValid = strDate => {
    return isValidDate(new Date(strDate)) && isSupportedDateFormat(strDate);
};

export const dateFormat = d => {
    let hours = d.getHours();
    let minutes = d.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    let month = d.getMonth() + 1;
    let day = d.getDate();
    day = day < 10 ? `0${day}` : day;
    month = month < 10 ? `0${month}` : month;
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    hours = hours < 10 ? `0${hours}` : hours;
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${month}/${day}/${d.getFullYear()} ${hours}:${minutes} ${ampm}`;
};
export const stringFormat = s => {
    return s || "&mdash;";
};
// ========== Transaction methods =====================
export function getCurrentMonthEndDateString(dateString) {
    const date = new Date(dateString);
    const monthEndDate = new Date(new Date(date.getFullYear(), date.getMonth() + 1, 1).getTime() - 24 * 60 * 60 * 1000);
    return getDateString(monthEndDate);
}

export function isMonthEndDate(dateString) {
    const monthEndDate = getCurrentMonthEndDateString(dateString);
    return new Date(dateString).getDate() === new Date(monthEndDate).getDate();
}

const getNextMonthEndDateString = dateString => {
    const date = new Date(dateString);
    const nextMonthEndDate = new Date(new Date(date.getFullYear(), date.getMonth() + 2, 1).getTime() - 24 * 60 * 60 * 1000);
    return getDateString(nextMonthEndDate);
};

// portfolio start date
export function getNonOneTimeStartDate(startDate) {
    return isMonthEndDate(startDate) ? getNextMonthEndDateString(startDate) : getCurrentMonthEndDateString(startDate);
}

const getLastMonthEndDateString = dateString => {
    const date = new Date(dateString);
    const lastMonthEndDate = new Date(new Date(date.getFullYear(), date.getMonth(), 1).getTime() - 24 * 60 * 60 * 1000);
    return getDateString(lastMonthEndDate);
};

// portfolio end date
export function getNonOneTimeEndDate(endDate) {
    return isMonthEndDate(endDate) ? getDateString(endDate) : getLastMonthEndDateString(endDate);
}

export function getPortfolioDate(portfolio) {
    let startDate, endDate;
    portfolio.securities.forEach(holding => {
        if (!startDate || startDate > new Date(holding.startDate)) {
            startDate = new Date(holding.startDate);
        }
        if (!endDate || endDate < new Date(holding.endDate)) {
            endDate = new Date(holding.endDate);
        }
    });
    return {
        startDate,
        endDate
    };
}
export function checkValidDate(inputDate, portfolioStartDate) {
    const checkDate = typeof inputDate === "string" ? new Date(inputDate) : inputDate;
    const isMonthAfter = checkDate.getMonth() >= portfolioStartDate.getMonth();
    const isYearAfter = checkDate.getFullYear() > portfolioStartDate.getFullYear();
    const isSameYear = checkDate.getFullYear() === portfolioStartDate.getFullYear();
    return (isMonthAfter && isSameYear) || (!isMonthAfter && isYearAfter) || (isMonthAfter && isYearAfter);
}
export function isValidTransactionRecord(startDate, portfolioDate) {
    const currentStartDate = new Date(startDate);
    const { startDate: portfolioStartDate, endDate: portfolioEndDate } = portfolioDate;
    const afterStarDate = checkValidDate(startDate, portfolioStartDate);
    return afterStarDate && currentStartDate <= portfolioEndDate;
}

export function showNotification(store, text, variation = "success", size = "small", title = "", persistent = false) {
    const notification = {
        size: size,
        variation: variation,
        title: title,
        persistent: persistent,
        text: text
    };
    store.dispatch("notifications/showNotification", notification);
}

export function isEqualTimePeriod(portfolioList) {
    const { startDate: startDate0, endDate: endDate0 } = getPortfolioDate(portfolioList[0]);
    const { startDate: startDate1, endDate: endDate1 } = getPortfolioDate(portfolioList[1]);
    return getDateString(startDate0) === getDateString(startDate1) && getDateString(endDate0) === getDateString(endDate1);
}

export function isValidCommonEndDate(portfolioList) {
    const securities = portfolioList.reduce((prev, portfolio) => {
        return prev.concat(portfolio.securities);
    }, []);
    const { earliestCommonDate, commonEndDate } = getSecuritiesDates(securities);
    return new Date(earliestCommonDate) < new Date(commonEndDate);
}

// portfolio compare : sync

const ignoredProps = [
    "id",
    "portfolioId",
    "name",
    "securities",
    "synced",
    "firstSynced",
    "totalInvestAmount",
    "allocateBy",
    "pctAllocateBy",
    "priceType",
    "transfers",
    "currency"
];
export function checkRebalancing(portfolio) {
    if (!portfolio.rebalance) {
        return true;
    }
    return portfolio.rebalance.rebalancingTo !== 1;
}
export function checkContributions(portfolio1, portfolio2) {
    const timePeriod = getPortfolioDate(portfolio2);
    if (portfolio1.subSequentSetting && portfolio1.subSequentSetting.subSequents) {
        const { subSequents } = portfolio1.subSequentSetting;
        const transactions = subSequents.filter(item => item.frequency === 99);
        return transactions.every(item => isValidTransactionRecord(item.startDate, timePeriod));
    }
    return false;
}

function normalizeSubsequents(portfolio) {
    if (portfolio.subSequentSetting && portfolio.subSequentSetting.subSequents) {
        portfolio.subSequentSetting.subSequents.forEach(item => delete item.visible);
    }
}
function normalizeBenchmark(portfolio) {
    const { benchmarkType = 0, benchmarkHoldings = [] } = portfolio.benchmark || {};
    if (portfolio.benchmark && benchmarkType === 0 && benchmarkHoldings.length > 0) {
        //ignore benchmark index name/type comparison.
        portfolio.benchmark.benchmarkHoldings.forEach(item => {
            delete item.securityName;
            delete item.securityType;
        });
    }
}

function normalizeFees(portfolio, portfolio2) {
    const { feeSetting, securities } = portfolio;
    const { includeTransactionFee } = feeSetting || {};
    if (includeTransactionFee) {
        const hasSecurityFeeSetting = securities.every(security => typeof security.fees !== "undefined");
        const isEqualFontLoad = hasSecurityFeeSetting ? checkEqualFrontLoadFees(securities) : false;
        const isEqualRedempCharge = hasSecurityFeeSetting ? isEqualRedemptionChargeFee(securities) : false;
        if (!isEqualFontLoad) {
            delete portfolio.feeSetting.frontLoadType;
            delete portfolio2.feeSetting.frontLoadType;
        }
        if (!isEqualRedempCharge) {
            delete portfolio.feeSetting.redemptionCharge;
            delete portfolio2.feeSetting.redemptionCharge;
        }
    }
}

export function checkSyncedPortfolios(portfolioList) {
    const localIgnoredProps = [...ignoredProps];
    const portfolio1 = cloneDeep(portfolioList[0]);
    const portfolio2 = cloneDeep(portfolioList[1]);

    normalizeFees(portfolio1, portfolio2);
    localIgnoredProps.push(!checkRebalancing(portfolio1) ? "rebalance" : null);
    localIgnoredProps.push(!checkContributions(portfolio1, portfolio2) ? "subSequentSetting" : null);
    const updatedIngoredProps = localIgnoredProps.filter(Boolean);
    normalizeSubsequents(portfolio1);
    normalizeSubsequents(portfolio2);
    normalizeBenchmark(portfolio1);
    normalizeBenchmark(portfolio2);
    updatedIngoredProps.forEach(prop => delete portfolio1[prop]);
    updatedIngoredProps.forEach(prop => delete portfolio2[prop]);
    return isEqual(portfolio1, portfolio2);
}

export function loadScript(src, onLoadFn) {
    const script = document.createElement("script");
    script.src = src;
    script.onload = onLoadFn;
    const currentScript = document.currentScript;
    const nextSibling = currentScript.nextSibling;
    if (nextSibling) {
        nextSibling.parentNode.insertBefore(script, nextSibling);
    } else {
        currentScript.parentNode.appendChild(script);
    }
}

export async function loadAWSECoreFromCdn() {
    return new Promise(resolve => {
        loadScript(`${window.__awse_mfe__.MFE_PROVIDER}/microfrontends/awse-mwc/awse_core/main.js`, async () => {
            await (window.awse_cdn_promise || Promise.resolve());
            resolve();
        });
    });
}
